import React, { useState } from "react";
import { withRouter } from "react-router-dom";
import { Form, Icon, Input, Button, Card, Row, Col, notification } from "antd";
import firebase from "../../services/firebaseConfig";
import { useFreire } from "../../utils/freireContext";
import * as texts from "./locales";
import { getUserAdminData } from "../../services/adminData";
import PasswordReset from "./PasswordReset";
import { isWemob } from "../../utils/is-wemob";
import api from "../../services/api";
import { setAmplitudeUser, trackEvent } from "../../analytics";

const { LOCAL } = firebase.auth.Auth.Persistence;

const Login = ({ history }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const { freire } = useFreire();

  const fetchAdminDataWithRetry = async (email, maxRetries = 2) => {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await getUserAdminData(email);
      } catch (error) {
        const status = error?.response?.status;

        if (status === 403 || status === 404) {
          return null;
        }

        const isClientError = status >= 400 && status < 500;

        if (isClientError || attempt >= maxRetries) {
          throw error;
        }

        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
  };

  // Autenticou no Firebase mas não passou na autorização do admin. Sem este
  // signOut a sessão continua viva e o `PrivateRoute`, que só checa a sessão do
  // Firebase, deixa entrar digitando `/dashboard` na barra de endereço. O CPMS
  // faz o mesmo quando o `verify-session` rejeita (`useVerifySession`).
  // Um signOut que rejeita não pode engolir a mensagem que o usuário precisa
  // ver, por isso a falha só vai para o console.
  const denyAccess = async (description) => {
    try {
      await firebase.auth().signOut();
    } catch (signOutError) {
      console.error("sign-out after a denied login failed:", signOutError);
    }

    notification.error({ message: "Ops...", description });
  };

  // Identifica antes de emitir: o `PrivateRoute` só monta depois do push de
  // rota, e sem isto o Sign_in sairia com o user_id de quem usou a máquina
  // antes, restaurado do storage pelo SDK. Chamar nos dois lugares é
  // idempotente. O nome do evento é o mesmo do CPMS.
  const identifyAndTrackSignIn = () => {
    setAmplitudeUser(firebase.auth().currentUser || {});
    trackEvent("Sign_in");
  };

  const handleWemobRedirect = async () => {
    const {
      data: { registrationStatus },
    } = await api.get("/company/registration-status");

    if (registrationStatus !== "active") {
      history.push("/ativacao", { registrationStatus });
      return true;
    }
    return false;
  };

  const handlerLogin = async (event) => {
    event.preventDefault();

    if (!email || !password) return;

    setLoading(true);

    try {
      await firebase.auth().setPersistence(LOCAL);
      await firebase.auth().signInWithEmailAndPassword(email, password);

      const tokenInfo = await firebase
        .auth()
        .currentUser.getIdTokenResult(true);

      // O Sign_in sai aqui também: este early return leva o admin WeMob cuja
      // empresa ainda não está ativa para `/ativacao`, que é exatamente a
      // coorte que o wizard de ativação existe para medir.
      if (isWemob && (await handleWemobRedirect())) {
        identifyAndTrackSignIn();
        return;
      }

      let adminData;
      try {
        adminData = await fetchAdminDataWithRetry(tokenInfo.claims.email);
      } catch (adminError) {
        // Aqui a autorização não foi negada, ela não pôde ser verificada (5xx
        // que sobreviveu aos retries). O signOut é proposital: o `PrivateRoute`
        // honra a sessão do Firebase sem rechecar o `is_admin`. Por isso a
        // mensagem diz que é temporário e pede nova tentativa.
        console.error("admin-data check failed:", adminError);
        await denyAccess(freire(texts.ADMIN_CHECK_FAILED));
        return;
      }

      if (tokenInfo && adminData?.is_admin) {
        identifyAndTrackSignIn();

        notification.success({
          message: freire(texts.ALL_RIGHT),
          description: freire(texts.LOGIN_DONE),
        });
        history.push({ pathname: "/dashboard", state: { from: "login" } });
      } else {
        await denyAccess(freire(texts.ONLY_ADMIN_CAN));
      }
    } catch (error) {
      console.log(error);
      notification.error({
        message: freire(texts.WENT_WONG),
        description: freire(texts.CHECK_AUTH),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Row
        style={{
          display: "flex",
          justifyContent: "center",
          height: "100vh",
          alignItems: "center",
        }}
      >
        <Col xs={20} sm={16} md={12} lg={8} xl={5}>
          <Card
            headStyle={{
              textAlign: "center",
              backgroundColor: "#001529",
              color: "white",
            }}
            title={
              showPasswordReset
                ? freire(texts.RESET_PASSWORD_TITLE)
                : freire(texts.MAKE_LOGIN)
            }
          >
            {showPasswordReset ? (
              <PasswordReset
                initialEmail={email}
                onBackToLogin={(resetEmail) => {
                  if (resetEmail) setEmail(resetEmail);
                  setPassword("");
                  setShowPasswordReset(false);
                }}
              />
            ) : (
              <Form onSubmit={handlerLogin}>
                <Form.Item>
                  <Input
                    prefix={
                      <Icon type="user" style={{ color: "rgba(0,0,0,.25)" }} />
                    }
                    size="large"
                    type="email"
                    placeholder={freire(texts.EMAIL)}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </Form.Item>
                <Form.Item>
                  <Input.Password
                    prefix={
                      <Icon type="lock" style={{ color: "rgba(0,0,0,.25)" }} />
                    }
                    size="large"
                    placeholder={freire(texts.PASSWORD)}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </Form.Item>
                <Form.Item>
                  <Button
                    htmlType="submit"
                    onClick={handlerLogin}
                    loading={loading}
                    style={{ width: "100%", backgroundColor: "#001529" }}
                    type="primary"
                  >
                    {freire(texts.ENTER)}
                  </Button>
                </Form.Item>
                <div style={{ textAlign: "center" }}>
                  <Button
                    type="link"
                    onClick={() => setShowPasswordReset(true)}
                    style={{ padding: 0 }}
                  >
                    {freire(texts.FORGOT_PASSWORD)}
                  </Button>
                </div>
                <div style={{ textAlign: "center", marginTop: 8 }}>
                  <span style={{ marginRight: 4 }}>
                    {freire(texts.DONT_HAVE_ACCOUNT)}
                  </span>
                  <Button
                    type="link"
                    onClick={() => history.push("/onboarding")}
                    style={{ padding: 0 }}
                  >
                    {freire(texts.SIGN_UP)}
                  </Button>
                </div>
              </Form>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default withRouter(Login);
